import React, { useState, useCallback, useRef, useEffect } from 'react';
import '../styles/HistorySimulator.css';
import { HistoryTimeline } from './history/HistoryTimeline';
import { CausalityVisualizer } from './history/CausalityVisualizer';
import { EventExplorer } from './history/EventExplorer';
import { BranchingNarrative } from './history/BranchingNarrative';

interface SimulationProgress {
  status: 'idle' | 'running' | 'complete' | 'error';
  currentYear: number;
  startYear: number;
  endYear: number;
  progress: number;
  eventsGenerated: number;
  entitiesCreated: number;
  warsFought: number;
  battlesFought: number;
  treatiesSigned: number;
  causalityChainsTriggered: number;
  message: string;
  error?: string;
}

interface HistoryEvent {
  id: string;
  year: number;
  category: string;
  description: string;
  significance: number;
  tags: string[];
  actors?: string[];
  location?: string;
}

interface HistoryEntity {
  id: string;
  type: string;
  name: string;
  createdYear: number;
  currentStatus: string;
  description?: string;
  relatedEvents?: string[];
}

interface TimelineData {
  events: HistoryEvent[];
  entities: HistoryEntity[];
  summary: string;
}


interface DetailItem {
  type: 'event' | 'entity';
  id: string;
  data: HistoryEvent | HistoryEntity;
}

export const HistorySimulator: React.FC = () => {
  const [progress, setProgress] = useState<SimulationProgress>({
    status: 'idle',
    currentYear: 0,
    startYear: 0,
    endYear: 0,
    progress: 0,
    eventsGenerated: 0,
    entitiesCreated: 0,
    warsFought: 0,
    battlesFought: 0,
    treatiesSigned: 0,
    causalityChainsTriggered: 0,
    message: 'Ready to simulate',
  });

  const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
  const [selectedTab, setSelectedTab] = useState<'progress' | 'timeline' | 'explorer' | 'causality' | 'narrative' | 'entities' | 'summary'>('progress');
  const [eventFilter, setEventFilter] = useState<string>('');
  const [entityFilter, setEntityFilter] = useState<string>('');
  const [selectedDetail, setSelectedDetail] = useState<DetailItem | null>(null);
  const [sortBy, setSortBy] = useState<'year' | 'significance'>('year');
  const [categoryFilter, setCategoryFilter] = useState<string>('');
  const [typeFilter, setTypeFilter] = useState<string>('');
  const [simulationSpeed, setSimulationSpeed] = useState<'slow' | 'normal' | 'fast' | 'instant'>('normal');
  const [eventFeed, setEventFeed] = useState<HistoryEvent[]>([]);
  const [selectedYear, setSelectedYear] = useState<number | null>(null);
  const [selectedEvent, setSelectedEvent] = useState<HistoryEvent | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);

  const startSimulation = useCallback(async () => {
    setEventFeed([]);
    setProgress({
      status: 'running',
      currentYear: 0,
      startYear: 0,
      endYear: 852,
      progress: 0,
      eventsGenerated: 0,
      entitiesCreated: 0,
      warsFought: 0,
      battlesFought: 0,
      treatiesSigned: 0,
      causalityChainsTriggered: 0,
      message: 'Starting simulation...',
    });

    // Close any existing connection
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    try {
      const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
      const speedParam = simulationSpeed === 'instant' ? '?instant=true' : '';
      const eventSource = new EventSource(`${apiUrl}/api/history/simulate${speedParam}`);

      eventSourceRef.current = eventSource;

      eventSource.addEventListener('progress', (event) => {
        const data = JSON.parse(event.data);
        setProgress((prev) => ({
          ...prev,
          ...data,
          status: 'running',
        }));
      });

      eventSource.addEventListener('event', (event) => {
        const eventData = JSON.parse(event.data);
        setEventFeed((prev) => [eventData, ...prev].slice(0, 100)); // Keep last 100 events
      });

      eventSource.addEventListener('complete', (event) => {
        const data = JSON.parse(event.data);
        setProgress((prev) => ({
          ...prev,
          ...data,
          status: 'complete',
          message: 'Simulation complete!',
        }));
        setTimelineData(data.timeline);
        eventSource.close();
        eventSourceRef.current = null;
      });

      eventSource.addEventListener('error', (event) => {
        console.error('Simulation error:', event);
        setProgress((prev) => ({
          ...prev,
          status: 'error',
          message: 'Simulation failed',
          error: 'Connection lost or server error',
        }));
        eventSource.close();
        eventSourceRef.current = null;
      });
    } catch (error) {
      console.error('Failed to start simulation:', error);
      setProgress((prev) => ({
        ...prev,
        status: 'error',
        message: 'Failed to start simulation',
        error: String(error),
      }));
    }
  }, [simulationSpeed]);

  useEffect(() => {
    return () => {
      if (eventSourceRef.current) {
        eventSourceRef.current.close();
      }
    };
  }, []);

  // Filter and sort events
  const filteredEvents = (timelineData?.events || []).filter((event) => {
    const matchesText = event.description.toLowerCase().includes(eventFilter.toLowerCase()) ||
      event.category.toLowerCase().includes(eventFilter.toLowerCase());
    const matchesCategory = !categoryFilter || event.category === categoryFilter;
    return matchesText && matchesCategory;
  }).sort((a, b) => {
    if (sortBy === 'significance') return b.significance - a.significance;
    return a.year - b.year;
  });

  // Filter and sort entities
  const filteredEntities = (timelineData?.entities || []).filter((entity) => {
    const matchesText = entity.name.toLowerCase().includes(entityFilter.toLowerCase()) ||
      entity.type.toLowerCase().includes(entityFilter.toLowerCase());
    const matchesType = !typeFilter || entity.type === typeFilter;
    return matchesText && matchesType;
  }).sort((a, b) => a.createdYear - b.createdYear);

  const significantEvents = filteredEvents.filter((e) => e.significance >= 7);
  
  // Get unique categories and types
  const categories = Array.from(new Set(timelineData?.events.map(e => e.category) || []));
  const types = Array.from(new Set(timelineData?.entities.map(e => e.type) || []));
  
  // Calculate statistics
  const eventsByCategory = (timelineData?.events || []).reduce((acc, event) => {
    acc[event.category] = (acc[event.category] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const entitiesByType = (timelineData?.entities || []).reduce((acc, entity) => {
    acc[entity.type] = (acc[entity.type] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  // Find related events for an entity
  const getRelatedEvents = (entityId: string): HistoryEvent[] => {
    return filteredEvents.filter(e => 
      e.actors?.includes(entityId) || e.description.includes(entityId)
    );
  };

  // Find related entities for an event
  const getRelatedEntities = (eventId: string): HistoryEntity[] => {
    const event = timelineData?.events.find(e => e.id === eventId);
    if (!event) return [];
    return filteredEntities.filter(e => 
      event.actors?.includes(e.id) || event.description.includes(e.name)
    );
  };

  return (
    <div className="history-simulator">
      {/* <div className="history-header">
        <h1>History Simulator</h1>
        <p>Simulate 1000+ years of world history with real-time progress tracking</p>
      </div> */}

      <div className="history-controls">
        <div className="speed-controls">
          <button
            className={`speed-button ${simulationSpeed === 'slow' ? 'active' : ''}`}
            onClick={() => setSimulationSpeed('slow')}
            title="Slow speed - detailed progress updates"
          >
            🐢 Slow
          </button>
          <button
            className={`speed-button ${simulationSpeed === 'normal' ? 'active' : ''}`}
            onClick={() => setSimulationSpeed('normal')}
            title="Normal speed - balanced updates"
          >
            ⚡ Normal
          </button>
          <button
            className={`speed-button ${simulationSpeed === 'fast' ? 'active' : ''}`}
            onClick={() => setSimulationSpeed('fast')}
            title="Fast speed - minimal updates"
          >
            🚀 Fast
          </button>
          <button
            className={`speed-button ${simulationSpeed === 'instant' ? 'active' : ''}`}
            onClick={() => setSimulationSpeed('instant')}
            title="Instant - jump to completion"
          >
            ⚡ Instant
          </button>
        </div>
        <button
          className="simulate-button"
          onClick={startSimulation}
          disabled={progress.status === 'running'}
        >
          {progress.status === 'running' ? 'Simulating...' : 'Start Simulation'}
        </button>
      </div>

      {progress.status !== 'idle' && (
        <div className="history-progress-panel">
          <div className="progress-header">
            <h2>Simulation Progress</h2>
            <span className={`status-badge ${progress.status}`}>{progress.status.toUpperCase()}</span>
          </div>

          <div className="progress-bar-container">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress.progress}%` }}
              />
            </div>
            <span className="progress-text">
              Year {progress.currentYear} / {progress.endYear} ({progress.progress.toFixed(1)}%)
            </span>
          </div>

          <div className="progress-message">{progress.message}</div>

          {progress.error && <div className="progress-error">{progress.error}</div>}

          <div className="progress-stats">
            <div className="stat">
              <span className="stat-label">Events Generated</span>
              <span className="stat-value">{progress.eventsGenerated}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Entities Created</span>
              <span className="stat-value">{progress.entitiesCreated}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Wars Fought</span>
              <span className="stat-value">{progress.warsFought}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Battles Fought</span>
              <span className="stat-value">{progress.battlesFought}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Treaties Signed</span>
              <span className="stat-value">{progress.treatiesSigned}</span>
            </div>
            <div className="stat">
              <span className="stat-label">Causality Chains</span>
              <span className="stat-value">{progress.causalityChainsTriggered}</span>
            </div>
          </div>
        </div>
      )}

      {/* Event Feed - shows during simulation */}
      {(progress.status === 'running' || eventFeed.length > 0) && (
        <div className="event-feed-panel">
          <div className="feed-header">
            <h2>Event Feed</h2>
            {progress.status === 'running' && <span className="live-indicator">● LIVE</span>}
          </div>
          <div className="event-feed">
            {eventFeed.length === 0 && progress.status === 'running' && (
              <div className="feed-empty">Waiting for events...</div>
            )}
            {eventFeed.map((event, index) => (
              <div key={`${event.id}-${index}`} className="feed-event">
                <div className="event-year">Year {event.year}</div>
                <div className="event-category">{event.category}</div>
                <div className="event-description">{event.description}</div>
                {event.significance >= 7 && (
                  <div className="event-significance">⭐ Significant</div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {timelineData && (
        <div className="history-results">
          <div className="results-tabs">
            <button
              className={`tab ${selectedTab === 'progress' ? 'active' : ''}`}
              onClick={() => setSelectedTab('progress')}
            >
              Progress
            </button>
            <button
              className={`tab ${selectedTab === 'timeline' ? 'active' : ''}`}
              onClick={() => setSelectedTab('timeline')}
            >
              Timeline
            </button>
            <button
              className={`tab ${selectedTab === 'explorer' ? 'active' : ''}`}
              onClick={() => setSelectedTab('explorer')}
            >
              Explorer
            </button>
            <button
              className={`tab ${selectedTab === 'causality' ? 'active' : ''}`}
              onClick={() => setSelectedTab('causality')}
            >
              Causality
            </button>
            <button
              className={`tab ${selectedTab === 'narrative' ? 'active' : ''}`}
              onClick={() => setSelectedTab('narrative')}
            >
              Narrative
            </button>
            <button
              className={`tab ${selectedTab === 'entities' ? 'active' : ''}`}
              onClick={() => setSelectedTab('entities')}
            >
              Entities ({filteredEntities.length})
            </button>
            <button
              className={`tab ${selectedTab === 'summary' ? 'active' : ''}`}
              onClick={() => setSelectedTab('summary')}
            >
              Summary
            </button>
          </div>

          <div className="results-content">
            {selectedTab === 'progress' && (
              <div className="tab-content">
                <div className="final-stats">
                  <h3>Final Statistics</h3>
                  <div className="stats-grid">
                    <div className="stat-box">
                      <span className="label">Total Events</span>
                      <span className="value">{progress.eventsGenerated}</span>
                    </div>
                    <div className="stat-box">
                      <span className="label">Total Entities</span>
                      <span className="value">{progress.entitiesCreated}</span>
                    </div>
                    <div className="stat-box">
                      <span className="label">Significant Events</span>
                      <span className="value">{significantEvents.length}</span>
                    </div>
                    <div className="stat-box">
                      <span className="label">Wars</span>
                      <span className="value">{progress.warsFought}</span>
                    </div>
                    <div className="stat-box">
                      <span className="label">Battles</span>
                      <span className="value">{progress.battlesFought}</span>
                    </div>
                    <div className="stat-box">
                      <span className="label">Treaties</span>
                      <span className="value">{progress.treatiesSigned}</span>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {selectedTab === 'timeline' && (
              <div className="tab-content">
                <HistoryTimeline
                  events={timelineData.events}
                  startYear={progress.startYear || 0}
                  endYear={progress.endYear || 852}
                  selectedYear={selectedYear}
                  onYearSelect={setSelectedYear}
                  onEventClick={(event) => {
                    setSelectedEvent(event);
                    setSelectedTab('narrative');
                  }}
                />
              </div>
            )}

            {selectedTab === 'explorer' && (
              <div className="tab-content">
                <EventExplorer
                  events={timelineData.events}
                  entities={timelineData.entities}
                  onEventClick={(event) => {
                    setSelectedEvent(event);
                    setSelectedTab('narrative');
                  }}
                  onEntityClick={(entity) => {
                    setSelectedDetail({ type: 'entity', id: entity.id, data: entity });
                  }}
                />
              </div>
            )}

            {selectedTab === 'causality' && (
              <div className="tab-content">
                <CausalityVisualizer
                  chains={[]}
                  allEvents={timelineData.events}
                  onEventClick={(event) => {
                    setSelectedEvent(event);
                    setSelectedTab('narrative');
                  }}
                />
              </div>
            )}

            {selectedTab === 'narrative' && (
              <div className="tab-content">
                <BranchingNarrative
                  events={timelineData.events}
                  entities={timelineData.entities}
                  startEvent={selectedEvent}
                  onEventSelect={setSelectedEvent}
                />
              </div>
            )}

            {selectedTab === 'entities' && (
              <div className="tab-content">
                <div className="filters-panel">
                  <div className="filter-group">
                    <input
                      type="text"
                      placeholder="Search events..."
                      value={eventFilter}
                      onChange={(e) => setEventFilter(e.target.value)}
                      className="filter-input"
                    />
                  </div>
                  <div className="filter-controls">
                    <div className="filter-item">
                      <label>Category:</label>
                      <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)} className="filter-select">
                        <option value="">All Categories</option>
                        {categories.map(cat => (
                          <option key={cat} value={cat}>{cat}</option>
                        ))}
                      </select>
                    </div>
                    <div className="filter-item">
                      <label>Sort:</label>
                      <select value={sortBy} onChange={(e) => setSortBy(e.target.value as 'year' | 'significance')} className="filter-select">
                        <option value="year">By Year</option>
                        <option value="significance">By Significance</option>
                      </select>
                    </div>
                  </div>
                </div>

                <div className="events-stats">
                  <div className="stat-mini">
                    <span className="label">Total Events</span>
                    <span className="value">{filteredEvents.length}</span>
                  </div>
                  <div className="stat-mini">
                    <span className="label">Significant (7+)</span>
                    <span className="value">{significantEvents.length}</span>
                  </div>
                  {Object.entries(eventsByCategory).map(([cat, count]) => (
                    <div key={cat} className="stat-mini">
                      <span className="label">{cat}</span>
                      <span className="value">{count}</span>
                    </div>
                  ))}
                </div>

                <div className="events-list">
                  {filteredEvents.length === 0 ? (
                    <p className="empty-message">No events match your filter</p>
                  ) : (
                    filteredEvents.map((event) => (
                      <div key={event.id} className={`event-item significance-${event.significance}`} onClick={() => setSelectedDetail({ type: 'event', id: event.id, data: event })}>
                        <div className="event-header">
                          <span className="event-year">Year {event.year}</span>
                          <span className="event-category">{event.category}</span>
                          <span className="event-significance">★ {event.significance}/10</span>
                        </div>
                        <p className="event-description">{event.description}</p>
                        <div className="event-meta">
                          <div className="event-tags">
                            {event.tags.slice(0, 3).map((tag) => (
                              <span key={tag} className="tag">{tag}</span>
                            ))}
                            {event.tags.length > 3 && <span className="tag">+{event.tags.length - 3}</span>}
                          </div>
                          <button className="link-button">View Details →</button>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            )}

            {selectedTab === 'entities' && (
              <div className="tab-content">
                <div className="filters-panel">
                  <div className="filter-group">
                    <input
                      type="text"
                      placeholder="Search entities..."
                      value={entityFilter}
                      onChange={(e) => setEntityFilter(e.target.value)}
                      className="filter-input"
                    />
                  </div>
                  <div className="filter-controls">
                    <div className="filter-item">
                      <label>Type:</label>
                      <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className="filter-select">
                        <option value="">All Types</option>
                        {types.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>

                <div className="entities-stats">
                  <div className="stat-mini">
                    <span className="label">Total Entities</span>
                    <span className="value">{filteredEntities.length}</span>
                  </div>
                  {Object.entries(entitiesByType).map(([type, count]) => (
                    <div key={type} className="stat-mini">
                      <span className="label">{type}</span>
                      <span className="value">{count}</span>
                    </div>
                  ))}
                </div>

                <div className="entities-list">
                  {filteredEntities.length === 0 ? (
                    <p className="empty-message">No entities match your filter</p>
                  ) : (
                    filteredEntities.map((entity) => {
                      const relatedEvents = getRelatedEvents(entity.id);
                      return (
                        <div key={entity.id} className="entity-item" onClick={() => setSelectedDetail({ type: 'entity', id: entity.id, data: entity })}>
                          <div className="entity-header">
                            <span className="entity-name">{entity.name}</span>
                            <span className="entity-type">{entity.type}</span>
                          </div>
                          <div className="entity-details">
                            <span className="detail">Created: Year {entity.createdYear}</span>
                            <span className="detail">Status: {entity.currentStatus}</span>
                            <span className="detail">Events: {relatedEvents.length}</span>
                          </div>
                          <button className="link-button">View Details →</button>
                        </div>
                      );
                    })
                  )}
                </div>
              </div>
            )}

            {selectedTab === 'summary' && (
              <div className="tab-content">
                <div className="summary-container">
                  <div className="summary-header">
                    <h2>World History Summary</h2>
                    <p className="summary-subtitle">A comprehensive overview of {progress.endYear} years of simulated history</p>
                  </div>

                  <div className="summary-grid">
                    <div className="summary-card overview-card">
                      <h3>📊 Overview</h3>
                      <div className="summary-stat">
                        <span className="label">Time Period:</span>
                        <span className="value">Year 0 - Year {progress.endYear}</span>
                      </div>
                      <div className="summary-stat">
                        <span className="label">Total Events:</span>
                        <span className="value">{progress.eventsGenerated}</span>
                      </div>
                      <div className="summary-stat">
                        <span className="label">Total Entities:</span>
                        <span className="value">{progress.entitiesCreated}</span>
                      </div>
                      <div className="summary-stat">
                        <span className="label">Significant Events:</span>
                        <span className="value">{significantEvents.length}</span>
                      </div>
                    </div>

                    <div className="summary-card events-card">
                      <h3>📜 Event Breakdown</h3>
                      {Object.entries(eventsByCategory).length === 0 ? (
                        <p className="empty-message">No events generated</p>
                      ) : (
                        Object.entries(eventsByCategory).map(([category, count]) => (
                          <div key={category} className="summary-stat">
                            <span className="label">{category}:</span>
                            <span className="value">{count}</span>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="summary-card entities-card">
                      <h3>🏛️ Entity Breakdown</h3>
                      {Object.entries(entitiesByType).length === 0 ? (
                        <p className="empty-message">No entities created</p>
                      ) : (
                        Object.entries(entitiesByType).map(([type, count]) => (
                          <div key={type} className="summary-stat">
                            <span className="label">{type}:</span>
                            <span className="value">{count}</span>
                          </div>
                        ))
                      )}
                    </div>

                    <div className="summary-card conflict-card">
                      <h3>⚔️ Conflict Statistics</h3>
                      <div className="summary-stat">
                        <span className="label">Wars:</span>
                        <span className="value">{progress.warsFought || 'None recorded'}</span>
                      </div>
                      <div className="summary-stat">
                        <span className="label">Battles:</span>
                        <span className="value">{progress.battlesFought || 'None recorded'}</span>
                      </div>
                      <div className="summary-stat">
                        <span className="label">Treaties:</span>
                        <span className="value">{progress.treatiesSigned || 'None recorded'}</span>
                      </div>
                      <div className="summary-stat">
                        <span className="label">Causality Chains:</span>
                        <span className="value">{progress.causalityChainsTriggered || 'None recorded'}</span>
                      </div>
                    </div>
                  </div>

                  <div className="summary-section">
                    <h3>🌟 Most Significant Events</h3>
                    <div className="significant-events">
                      {significantEvents.slice(0, 10).length === 0 ? (
                        <p className="empty-message">No significant events recorded</p>
                      ) : (
                        significantEvents.slice(0, 10).map((event) => (
                          <div key={event.id} className="significant-event-item" onClick={() => setSelectedDetail({ type: 'event', id: event.id, data: event })}>
                            <div className="event-marker">
                              <span className="year-badge">Year {event.year}</span>
                              <span className="significance-badge">★ {event.significance}</span>
                            </div>
                            <div className="event-content">
                              <h4>{event.category}</h4>
                              <p>{event.description}</p>
                              <button className="link-button">Read More →</button>
                            </div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  <div className="summary-section">
                    <h3>🏛️ Key Entities</h3>
                    <div className="key-entities">
                      {filteredEntities.slice(0, 8).length === 0 ? (
                        <p className="empty-message">No entities to display</p>
                      ) : (
                        filteredEntities.slice(0, 8).map((entity) => {
                          const relatedEvents = getRelatedEvents(entity.id);
                          return (
                            <div key={entity.id} className="key-entity-card" onClick={() => setSelectedDetail({ type: 'entity', id: entity.id, data: entity })}>
                              <div className="entity-icon">{entity.type === 'faction' ? '⚔️' : entity.type === 'sanctum' ? '🏛️' : '👤'}</div>
                              <h4>{entity.name}</h4>
                              <p className="entity-type">{entity.type}</p>
                              <p className="entity-info">Founded: Year {entity.createdYear}</p>
                              <p className="entity-info">Events: {relatedEvents.length}</p>
                              <button className="link-button">View Profile →</button>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>

                  {timelineData.summary && (
                    <div className="summary-section">
                      <h3>📖 Narrative Summary</h3>
                      <div className="narrative-box">
                        <pre className="summary-text">{timelineData.summary}</pre>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {selectedDetail && (
            <div className="detail-panel">
              <div className="detail-header">
                <button className="close-button" onClick={() => setSelectedDetail(null)}>✕</button>
                <h2>
                  {selectedDetail.type === 'event' 
                    ? `Event: ${(selectedDetail.data as HistoryEvent).category}`
                    : `Entity: ${(selectedDetail.data as HistoryEntity).name}`
                  }
                </h2>
              </div>

              {selectedDetail.type === 'event' && (
                <div className="detail-content">
                  {(() => {
                    const event = selectedDetail.data as HistoryEvent;
                    return (
                      <>
                        <div className="detail-section">
                          <h3>Event Details</h3>
                          <div className="detail-info">
                            <div className="info-row">
                              <span className="label">Year:</span>
                              <span className="value">{event.year}</span>
                            </div>
                            <div className="info-row">
                              <span className="label">Category:</span>
                              <span className="value">{event.category}</span>
                            </div>
                            <div className="info-row">
                              <span className="label">Significance:</span>
                              <span className="value">★ {event.significance}/10</span>
                            </div>
                            <div className="info-row full-width">
                              <span className="label">Description:</span>
                              <p className="value">{event.description}</p>
                            </div>
                          </div>
                        </div>

                        {event.tags && event.tags.length > 0 && (
                          <div className="detail-section">
                            <h3>Tags</h3>
                            <div className="tags-list">
                              {event.tags.map(tag => (
                                <span key={tag} className="tag-badge">{tag}</span>
                              ))}
                            </div>
                          </div>
                        )}

                        {getRelatedEntities(event.id).length > 0 && (
                          <div className="detail-section">
                            <h3>Related Entities</h3>
                            <div className="related-list">
                              {getRelatedEntities(event.id).map(entity => (
                                <div key={entity.id} className="related-item" onClick={() => setSelectedDetail({ type: 'entity', id: entity.id, data: entity })}>
                                  <span className="related-name">{entity.name}</span>
                                  <span className="related-type">{entity.type}</span>
                                  <span className="link-arrow">→</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}

              {selectedDetail.type === 'entity' && (
                <div className="detail-content">
                  {(() => {
                    const entity = selectedDetail.data as HistoryEntity;
                    const relatedEvents = getRelatedEvents(entity.id);
                    return (
                      <>
                        <div className="detail-section">
                          <h3>Entity Profile</h3>
                          <div className="detail-info">
                            <div className="info-row">
                              <span className="label">Name:</span>
                              <span className="value">{entity.name}</span>
                            </div>
                            <div className="info-row">
                              <span className="label">Type:</span>
                              <span className="value">{entity.type}</span>
                            </div>
                            <div className="info-row">
                              <span className="label">Created:</span>
                              <span className="value">Year {entity.createdYear}</span>
                            </div>
                            <div className="info-row">
                              <span className="label">Status:</span>
                              <span className="value">{entity.currentStatus}</span>
                            </div>
                            {entity.description && (
                              <div className="info-row full-width">
                                <span className="label">Description:</span>
                                <p className="value">{entity.description}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {relatedEvents.length > 0 && (
                          <div className="detail-section">
                            <h3>Related Events ({relatedEvents.length})</h3>
                            <div className="related-events-list">
                              {relatedEvents.slice(0, 10).map(event => (
                                <div key={event.id} className="related-event-item" onClick={() => setSelectedDetail({ type: 'event', id: event.id, data: event })}>
                                  <div className="event-year-badge">Year {event.year}</div>
                                  <div className="event-info">
                                    <h4>{event.category}</h4>
                                    <p>{event.description.substring(0, 100)}...</p>
                                  </div>
                                  <span className="link-arrow">→</span>
                                </div>
                              ))}
                              {relatedEvents.length > 10 && (
                                <p className="more-items">+{relatedEvents.length - 10} more events</p>
                              )}
                            </div>
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
